import React from 'react'
import { css, cx } from '@emotion/css'
import { useIntl } from 'react-intl'
import { theme } from '@contrast-security-inc/design-system-foundations'
import {
  CsDropdownCollapseIcon,
  CsDropdownExpandIcon
} from '../../legacy-design-system/icons'
import { ProtectMode, protectModeShortcutI18nConfig } from './protect-mode'
import { Text } from '@contrast-security-inc/design-system-typography'
import { Badge, BadgeColors } from '@contrast-security-inc/design-system-badges'
import { IconBaseProps } from 'react-icon-base'

export const pmContainerDataTestID = 'mode-badge-container'
export const pmIconExpandDataTestID = 'mode-badge-icon-expand'
export const pmIconCollapseDataTestID = 'mode-badge-icon-collapse'

const badgeContainerCss = cx(
  css`
    padding: 0 2px;
    display: table-cell;
    width: 90px;
    min-width: 90px;
    max-width: 90px;
    line-height: 1.42857;
    border-radius: 2px;
    vertical-align: middle;
    text-align: center;
  `
)

const modeColorConfig: Record<ProtectMode, BadgeColors> = {
  OFF: 'neutral60',
  MONITORING: 'neutral90',
  BLOCKING: 'green50',
  BLOCKING_PERIMETER: 'green30',
  MONITOR_PERIMETER: 'neutral90'
}

interface ProtectModeBadgeProps {
  readonly mode: ProtectMode
  readonly isExpanded?: boolean
  readonly showDropdownStyle?: boolean
}

export function ProtectModeBadge({
  mode,
  isExpanded,
  showDropdownStyle = true
}: ProtectModeBadgeProps) {
  const modeLabel = useIntl().formatMessage({
    id: protectModeShortcutI18nConfig[mode]
  })

  const modeBgColor = modeColorConfig[mode]

  const DropdownIcon = (props: JSX.IntrinsicAttributes & IconBaseProps) =>
    isExpanded ? (
      <CsDropdownCollapseIcon
        data-testid={pmIconCollapseDataTestID}
        {...props}
      />
    ) : (
      <CsDropdownExpandIcon data-testid={pmIconExpandDataTestID} {...props} />
    )

  return (
    <Badge
      variant="Normal"
      color={modeBgColor}
      data-testid={pmContainerDataTestID}
      className={cx(
        badgeContainerCss,
        mode === 'OFF' &&
          css`
            opacity: 0.45;
          `
      )}
    >
      <>
        <Text
          font="labelSm"
          color={mode.startsWith('MONITOR') ? 'white' : 'black'}
          className={cx(
            'protect-mode-badge-text',
            css`
              text-align: center;
              vertical-align: middle;
              text-transform: uppercase;
              line-height: 1.42857;
            `
          )}
        >
          {modeLabel}
        </Text>
        {showDropdownStyle && (
          <DropdownIcon
            size={10}
            color={
              mode.startsWith('MONITOR') ? theme.color.white : theme.color.black
            }
            className={css`
              margin-left: 4px;
            `}
          />
        )}
      </>
    </Badge>
  )
}
